import * as React from 'react';
import { useCallback, useMemo } from 'react';
import * as R from 'ramda';
import AddDevItemCard from './AddDevItemCard';
import SurveyHeader from '../survey/SurveyHeader';
import { useMsgText } from '../../localization/hooks';
import { Str } from '../../localization/translationKeys';
import { css } from '@emotion/core';
import DevItemCard from './DevItemCard';
import DevPlanRemindersCard from './reminders/DevPlanRemindersCard';
import EmailDevPlanCard from './emailPlan/EmailDevPlanCard';
import { useRoutes, Routes } from '../../navigation/routes';
import { useAppState } from '../../core/redux/hooks';
import { FLEX_ROW, FLEX_COLUMN } from '../../layout/flexBox';
import LocalDiv from '../../localization/LocalDiv';
import { useDispatch } from 'react-redux';
import { setRemindersOnAction } from './reminders/setRemindersOnAction';
import { onMobileDevice } from '../../globalStyle/mediaQueries';
import { AnimatePresence, motion } from 'framer-motion';
import { useWindowSize } from '../../core/hooks/useWindowSize';
import MobileDevPlanActionBar from './MobileDevPlanActionBar';
import ClamShell from '../../layout/ClamShell';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import Spacer from '../../layout/Spacer';
import Msg from '../../localization/Msg';
import { COLORS } from '../../globalStyle/colors';
import { ProjStr } from '../messages/ProjStr';
import ProjMsg from '../messages/ProjMsg';
import { useMount } from '../../core/hooks/useMount';
import { setDevPlanViewedAction } from './api/setDevPlanViewedAction';
import { unreadCommentCount, devItemSubtitleLocal } from './collaboration/module';
import { useProjIdsForUserType } from '../../navigation/hooks';
import { Map } from '../../language/models';
import { DevItemComment } from './collaboration/models';
import { DevPlanItem, DevPlanAction } from './models';
import {
  useAddCollaboratorModalProps,
  useDeleteCollaboratorModalProps,
  useDevPlanCollaborationSetting, useCurrentCollaborator,
} from './collaboration/hooks';
import ConfirmationModal from '../../layout/modal/ConfirmationModal';
import CollaboratorsList from './collaboration/CollaboratorsList';
import { sendDevPlanAction } from './devPlanAction/api/sendDevPlanAction';
import { useCurrentUser } from '../../auth/hooks';
import CollaboratorPageHeader from './collaboration/CollaboratorPageHeader';
import PortalAutoLayout from '../../layout/PortalAutoLayout';
import { setDevPlanCollaboratorAcceptedAction } from './collaboration/api/setDevPlanCollaboratorAcceptedAction';


const itemAnimation = {
  initial:    { opacity: 0 },
  animate:    { opacity: 1 },
  exit:       { opacity: 0 },
  transition: { duration: 0.6, ease: [0.04, 0.62, 0.23, 0.98] },
};


const styles = {
  root:          css`
  `,
  header:        css`
    margin-bottom: 40px;
    
    ${onMobileDevice} {
      margin-top: 35px;
      margin-bottom: 35px;
    }
  `,
  allCards:      [
    FLEX_ROW,
    css`
      align-items: flex-start;
      flex-wrap: wrap;
      
      ${onMobileDevice} { 
        margin-bottom: 70px;
      }
    `,
  ],
  itemCards:     [
    FLEX_ROW,
    css`
      align-items: flex-start;
      flex-wrap: wrap;
      flex-shrink: 1;
      flex-grow: 1;
      flex-basis: 0;
    `,
  ],
  sharingCol:    css`
    width: 290px;
    flex-basis: 290px;
    flex-shrink: 0;
  `,
  card:          css`
    width: 270px;
    flex-basis: 270px;
    margin: 10px;
    flex-shrink: 0;
    flex-grow: 0;
    box-sizing: border-box;
    
    transition: all .5s ease;
  `,
  msgBar:        css`
      background-color: ${COLORS.WHITE};
      padding: 8px;
      border: 0 none;
      min-height: 35px;
      align-items: center;
      margin: auto 8px;
      border-radius: 8px;
  `,
  infoMsg:       css`
    color: ${COLORS.GREEN};
    background-color: transparent;
  `,
  instructions:  css`
    margin: 0 0 20px 8px;
  `,
  collaborators: [
    FLEX_COLUMN,
    css`
      margin: 10px;
    `,
  ],
};


type Props = {
  className?: string;
};


const renderDevItemCards = (
  devItems: DevPlanItem[],
  projectComments: Map<DevItemComment[]>,
  actions: Map<DevPlanAction[]>,
  routes: Routes,
  collaborationEnabled: boolean,
  isParticipant: boolean,
) => devItems.map(item => {
    const itemComments: DevItemComment[] | undefined = R.path([`${item.devItemId}`], projectComments);
    const itemActions: DevPlanAction[] | undefined   = R.path([`${item.devItemId}`], actions);

    const itemRouteTemplate = isParticipant ? routes.ViewPlanItem : routes.CollaboratorViewPlanItem;

    return (
      <motion.div key={item.devItemId} {...itemAnimation}>
        <DevItemCard
          item={item}
          itemLocation={itemRouteTemplate.replace(':itemId', `${item.devItemId}`)}
          actionCount={itemActions?.length ?? 0}
          collaborationEnabled={collaborationEnabled}
          isParticipant={isParticipant}
          unreadCommentCount={unreadCommentCount(itemComments ?? [])}
          css={styles.card}
        />
      </motion.div>
    );
  },
);


/**
 * DevPlanView: a component that renders the dev plan page
 *
 * PureComponent: true
 * Redux Connected: false
 */
const DevPlanView: React.FC<Props> = props => {
  const dispatch = useDispatch();

  const isParticipant = !!useCurrentUser();

  const { isMobile } = useWindowSize();

  const pageTitle = useMsgText(Str.DevPlan);

  const { participantId, projDisplayId, projId } = useProjIdsForUserType();

  const routes = useRoutes(participantId, projDisplayId);

  // Reminders
  const remindersOn    = useAppState(state => !!state.devPlan?.emailRemindersOn);
  const setRemindersOn = useCallback(
    (newValue: boolean) => dispatch(setRemindersOnAction(projId, participantId, newValue)),
    [participantId, projId, dispatch],
  );

  // Dev Items
  const devItems = useAppState(state => state.devPlan?.items ?? []);
  const actions  = useAppState(state => state.devPlan?.actionsByItemId);

  // Email Dev Plan
  const emailRequired = useAppState(
    state => !state.projectSummary?.devPlanEmailSent && !!state.projectSummary?.developmentPlanEmailLevelId,
  );

  const raterMap = useAppState(state => state.projectRaters?.[`${projId}`] ?? []);
  const raters   = useMemo(() => Object.values(raterMap), [raterMap]);

  const project      = useAppState(state => state.projectSummary);
  const itemsToTrack = project?.developmentPlanGoalsToTrack ?? 0;
  const showItems    = itemsToTrack > 0 && devItems.length < itemsToTrack;
  const showEmail    = (project?.developmentPlanEmailLevelId ?? 0) > 0 && !project?.devPlanEmailSent;

  const devPlanProjectId =  project?.devPlanProjectId ?? projId;

  useMount(() => {
    dispatch(setDevPlanViewedAction(true));

    if (!currentCollaborator) {
      return;
    }

    if (currentCollaborator.currentProject.accepted) {
      return;
    }

    dispatch(setDevPlanCollaboratorAcceptedAction(projId, participantId, currentCollaborator.email));
  });

  // Collaboration
  const collaborationEnabled = useDevPlanCollaborationSetting();
  const collaborators        = useAppState(state => state.devPlan?.collaborators?.[`${devPlanProjectId}`]);
  const comments             = useAppState(state => state.devPlan?.comments?.[`${devPlanProjectId}`]);

  const {
          addCollaboratorModalVisible,
          showAddCollaboratorModal,
          dismissAddCollaboratorModal,
          confirmAddCollaboratorMsg,
          onConfirmAddCollaborator,
        } = useAddCollaboratorModalProps(devPlanProjectId, participantId, dispatch);

  const {
          deleteCollaboratorModalVisible,
          showDeleteCollaboratorModal,
          dismissDeleteCollaboratorModal,
          confirmDeleteCollaboratorMsg,
          onConfirmDeleteCollaborator,
        } = useDeleteCollaboratorModalProps(devPlanProjectId, participantId, dispatch);

  const sendDevPlan = useCallback(
    (email: string, comment?: string) => {
      dispatch(sendDevPlanAction(projId, participantId, email, comment));
    },
    [dispatch, participantId, projId],
  );

  const onClickSendEmailBtn = useCallback(collaborationEnabled ? showAddCollaboratorModal : sendDevPlan, []);

  const sendEmailBtnMsg = useMemo(() => collaborationEnabled ? Str.InviteShare : Str.Send, [collaborationEnabled]);

  const participantCanAdd = useMemo(() => 
    project?.developmentPlanAddManual || project?.developmentPlanAddCompetenciesFromDevPlan,
    [project],
  )

  const dir = useAppState(state => state.locale.textDirection);

  const currentCollaborator = useCurrentCollaborator();

  const headerSubtitle = useMemo(
    () => devItemSubtitleLocal(dir, currentCollaborator?.currentProject) || '',
    [currentCollaborator, dir],
  );

  return (
    <PortalAutoLayout className={props.className} key={projDisplayId}>
      <div>
        {
          /* Page Header */
          isParticipant
          ? (
            <>
              <SurveyHeader title={`/ ${pageTitle}`} deadline={undefined} css={styles.header} />
              <LocalDiv css={styles.instructions}>
                <ProjMsg str={ProjStr.DevPlanInstructions} />
              </LocalDiv>
              <ClamShell
                show={isParticipant && (showItems || showEmail)}
                css={[FLEX_ROW, styles.msgBar]}
              >
                <FontAwesomeIcon icon={faExclamationTriangle} css={css`color: ${COLORS.GREEN};`} size={'2x'} />
                <Spacer sizePx={10} />
                <LocalDiv css={FLEX_COLUMN}>
                  {
                    showItems && showEmail
                      ? <Msg
                          str={collaborationEnabled ? Str.DevPlanNumberShareMsg : Str.DevPlanNumberEmailMsg}
                          replace={{ 'number': itemsToTrack, 'levelName': project?.devPlanEmailLevelName }}
                          css={styles.infoMsg}
                        />
                      : showItems
                        ? <Msg
                            str={Str.DevPlanNumberMsg}
                            replace={{ 'number': itemsToTrack }}
                            css={styles.infoMsg}
                          />
                        : <Msg
                            str={collaborationEnabled ? Str.DevPlanShareMsg : Str.DevPlanEmailMsg}
                            replace={{ 'levelName': project?.devPlanEmailLevelName }}
                            css={styles.infoMsg}
                          />
                  }
                </LocalDiv>
              </ClamShell>
            </>
          ) : (
            <>
              <CollaboratorPageHeader title={pageTitle} subtitle={headerSubtitle} />
              <Spacer sizePx={50} />
            </>
            )
        }
        <LocalDiv css={styles.allCards}>
          <div css={styles.itemCards}>
            <AnimatePresence>
              {
                renderDevItemCards(
                  devItems,
                  comments ?? {},
                  actions ?? {},
                  routes,
                  collaborationEnabled,
                  isParticipant,
                )
              }
              {
                isParticipant && participantCanAdd && (
                  <motion.div key='addItem' {...itemAnimation}>
                    <AddDevItemCard routes={routes} css={styles.card} />
                  </motion.div>
                )
              }
            </AnimatePresence>
          </div>

          {
            isParticipant && (
              isMobile
                ? (
                  <MobileDevPlanActionBar
                    collaborationEnabled={collaborationEnabled}
                    routes={routes}
                    onClickToAdd={null as any}
                  />
                )
                : (
                  <div css={styles.sharingCol}>
                    <DevPlanRemindersCard
                      remindersEnabled={remindersOn}
                      onChangeRemindersEnabled={setRemindersOn}
                      css={styles.card}
                    />
                    <EmailDevPlanCard
                      raters={raters}
                      participantId={participantId}
                      projectId={projId}
                      emailingRequired={emailRequired}
                      submitBtnMsg={sendEmailBtnMsg}
                      onClickSubmitBtn={onClickSendEmailBtn}
                      collaborationEnabled={collaborationEnabled}
                      css={styles.card}
                    />
                    <CollaboratorsList
                      projectId={projId}
                      collaborators={collaborators ?? []}
                      deleteCollaborator={showDeleteCollaboratorModal}
                      css={styles.collaborators}
                    />
                  </div>
                )
            )
          }
        </LocalDiv>
      </div>
      {
        addCollaboratorModalVisible && (
          <ConfirmationModal
            confirmationTxt={confirmAddCollaboratorMsg}
            primaryActionStr={Str.Yes}
            secondaryActionStr={Str.No}
            onSelectPrimary={onConfirmAddCollaborator}
            onSelectSecondary={dismissAddCollaboratorModal}
            onDismiss={dismissAddCollaboratorModal}
          />
        )
      }
      {
        deleteCollaboratorModalVisible && (
          <ConfirmationModal
            confirmationTxt={confirmDeleteCollaboratorMsg}
            primaryActionStr={Str.Yes}
            secondaryActionStr={Str.No}
            onSelectPrimary={onConfirmDeleteCollaborator}
            onSelectSecondary={dismissDeleteCollaboratorModal}
            onDismiss={dismissDeleteCollaboratorModal}
          />
        )
      }
    </PortalAutoLayout>
  );
};

export default DevPlanView;
